import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/base/Navigation';
import Footer from '../../components/base/Footer';

export default function Services() {
  const [selectedService, setSelectedService] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Our Services - Evolution Home | Premium Renovation Services';
  }, []);

  const services = [
    {
      id: 'kitchen',
      title: 'Kitchen Renovations',
      icon: 'ri-restaurant-line',
      description: 'Transform your kitchen into a culinary paradise with luxury finishes and modern appliances.',
      features: ['Custom Cabinetry', 'Premium Countertops', 'High-End Appliances', 'Smart Storage Solutions'],
      image: 'https://public.readdy.ai/ai/img_res/a2137555c46e773467f45a7d0cb5e0ea.jpg'
    },
    {
      id: 'bathroom',
      title: 'Bathroom Remodeling',
      icon: 'ri-drop-line',
      description: 'Create a spa-like retreat with elegant fixtures and premium materials.',
      features: ['Luxury Fixtures', 'Custom Vanities', 'Premium Tiling', 'Heated Floors'],
      image: 'https://readdy.ai/api/search-image?query=luxury%20bathroom%20remodeling%20service%20with%20gold%20fixtures%20and%20black%20marble%2C%20spa-like%20design%2C%20premium%20materials%2C%20elegant%20vanity%2C%20professional%20renovation%20work&width=600&height=400&seq=service-bathroom&orientation=landscape'
    },
    {
      id: 'flooring',
      title: 'Premium Flooring',
      icon: 'ri-layout-grid-line',
      description: 'High-quality flooring solutions from hardwood to luxury vinyl and tile.',
      features: ['Hardwood Installation', 'Luxury Vinyl', 'Natural Stone', 'Custom Patterns'],
      image: 'https://readdy.ai/api/search-image?query=luxury%20flooring%20installation%20service%20with%20premium%20hardwood%20and%20stone%20materials%2C%20professional%20craftsmanship%2C%20elegant%20patterns%2C%20sophisticated%20interior%20design&width=600&height=400&seq=service-flooring&orientation=landscape'
    },
    {
      id: 'tiling',
      title: 'Expert Tiling',
      icon: 'ri-grid-line',
      description: 'Precision tile installation for walls, floors, and custom feature areas.',
      features: ['Natural Stone', 'Ceramic & Porcelain', 'Mosaic Designs', 'Waterproofing'],
      image: 'https://readdy.ai/api/search-image?query=expert%20tiling%20service%20with%20luxury%20materials%2C%20precision%20installation%2C%20natural%20stone%20and%20ceramic%20tiles%2C%20professional%20craftsmanship%2C%20elegant%20patterns%20and%20designs&width=600&height=400&seq=service-tiling&orientation=landscape'
    },
    {
      id: 'painting',
      title: 'Professional Painting',
      icon: 'ri-brush-line',
      description: 'Interior and exterior painting with premium paints and expert techniques.',
      features: ['Interior Painting', 'Exterior Painting', 'Decorative Finishes', 'Color Consultation'],
      image: 'https://readdy.ai/api/search-image?query=professional%20painting%20service%20with%20premium%20finishes%2C%20interior%20and%20exterior%20work%2C%20sophisticated%20color%20schemes%2C%20expert%20craftsmanship%2C%20elegant%20home%20transformation&width=600&height=400&seq=service-painting&orientation=landscape'
    },
    {
      id: 'plumbing',
      title: 'Plumbing Services',
      icon: 'ri-tools-line',
      description: 'Complete plumbing solutions for renovations and new installations.',
      features: ['Pipe Installation', 'Fixture Upgrades', 'Water Systems', 'Emergency Repairs'],
      image: 'https://readdy.ai/api/search-image?query=professional%20plumbing%20service%20installation%20with%20luxury%20fixtures%2C%20modern%20pipe%20systems%2C%20expert%20craftsmanship%2C%20premium%20bathroom%20and%20kitchen%20plumbing%20work&width=600&height=400&seq=service-plumbing&orientation=landscape'
    },
    {
      id: 'electrical',
      title: 'Electrical Work',
      icon: 'ri-flashlight-line',
      description: 'Licensed electrical services for all your renovation and upgrade needs.',
      features: ['Wiring & Rewiring', 'Smart Home Systems', 'Lighting Design', 'Panel Upgrades'],
      image: 'https://readdy.ai/api/search-image?query=professional%20electrical%20service%20installation%20with%20modern%20lighting%20systems%2C%20smart%20home%20technology%2C%20premium%20fixtures%2C%20expert%20electrical%20work%2C%20sophisticated%20interior%20lighting&width=600&height=400&seq=service-electrical&orientation=landscape'
    },
    {
      id: 'commercial',
      title: 'Commercial Units',
      icon: 'ri-building-line',
      description: 'Professional renovation services for offices, retail spaces, and commercial properties.',
      features: ['Office Renovations', 'Retail Spaces', 'Restaurant Buildouts', 'Compliance Updates'],
      image: 'https://readdy.ai/api/search-image?query=commercial%20renovation%20service%20with%20modern%20office%20and%20retail%20spaces%2C%20professional%20buildouts%2C%20luxury%20business%20interiors%2C%20sophisticated%20commercial%20design&width=600&height=400&seq=service-commercial&orientation=landscape'
    },
    {
      id: 'full',
      title: 'Full Home Renovations',
      icon: 'ri-home-line',
      description: 'Complete home transformations from concept to completion.',
      features: ['Design & Planning', 'Structural Work', 'All Trades Coordination', 'Project Management'],
      image: 'https://readdy.ai/api/search-image?query=full%20home%20renovation%20service%20showcase%20with%20complete%20house%20transformation%2C%20luxury%20interior%20design%2C%20premium%20materials%2C%20professional%20project%20management%2C%20elegant%20home%20makeover&width=600&height=400&seq=service-full&orientation=landscape'
    }
  ];

  const process = [
    { step: 1, title: 'Consultation', desc: 'Free in-home consultation and project assessment' },
    { step: 2, title: 'Design', desc: 'Custom design plans and 3D visualizations' },
    { step: 3, title: 'Proposal', desc: 'Detailed proposal with timeline and pricing' },
    { step: 4, title: 'Execution', desc: 'Professional construction with quality control' },
    { step: 5, title: 'Completion', desc: 'Final walkthrough and project handover' }
  ];

  const faqs = [
    { question: 'How long does a typical renovation take?', answer: 'Project timelines vary based on scope and complexity. Kitchen renovations typically take 1-62weeks, bathrooms 1-2 weeks, and full home renovations 2-5 months.' },
    { question: 'Do you provide design services?', answer: 'Yes, we offer comprehensive design services including material selection and space planning as part of our renovation packages.' },
    { question: 'Are you licensed and insured?', answer: 'Absolutely. We are fully licensed, bonded, and insured. All our work comes with comprehensive warranties for your peace of mind.' },
    { question: 'Can you work within my budget?', answer: 'We work with various budgets and will provide options to maximize value. During consultation, we\'ll discuss budget-friendly alternatives without compromising quality.' },
    { question: 'Do you handle permits?', answer: 'Yes, we handle all necessary permits and inspections as part of our comprehensive service. This ensures your project meets all local building codes.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=elegant%20luxury%20home%20renovation%20workspace%20with%20premium%20materials%20and%20tools%2C%20sophisticated%20interior%20design%20samples%2C%20high-end%20construction%20equipment%2C%20professional%20craftsmanship%20display%2C%20modern%20architectural%20elements&width=1920&height=800&seq=services-hero-new&orientation=landscape')`
        }}
      >
        <div className="text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Services</h1>
          <p className="text-xl text-gray-200">Premium renovation services for every space</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Comprehensive Renovation Services</h2>
            <p className="text-xl text-gray-600">From single rooms to complete home transformations</p>
            <p className="text-lg text-red-600 mt-2 font-medium">Click any service card to view details</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-red-600/20"
                onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                      <i className={`${service.icon} text-white text-xl`}></i>
                    </div>
                    <div className="w-8 h-8 bg-red-600/10 rounded-full flex items-center justify-center group-hover:bg-red-600/20 transition-all duration-300">
                      <i className={`ri-${selectedService === service.id ? 'subtract' : 'add'}-line text-red-600`}></i>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  
                  <div className="text-sm text-red-600 font-medium group-hover:text-red-700 transition-colors">
                    {selectedService === service.id ? 'Click to collapse' : 'Click to expand details'}
                  </div>
                  
                  {selectedService === service.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 animate-fadeIn">
                      <h4 className="font-semibold text-black mb-2">Features Include:</h4>
                      <ul className="space-y-1">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <div className="w-4 h-4 flex items-center justify-center mr-2">
                              <i className="ri-check-line text-red-600"></i>
                            </div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Our Process</h2>
            <p className="text-xl text-gray-600">How we bring your vision to life</p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-red-600/20"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {process.map((step, index) => (
                <div key={index} className="text-center relative">
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl relative z-10">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-black mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Get answers to common renovation questions</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                >
                  <span className="font-semibold text-black">{faq.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-${openAccordion === index ? 'subtract' : 'add'}-line text-red-600`}></i>
                  </div>
                </button>
                {openAccordion === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Get a free consultation and see how we can transform your space
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
              onClick={() => navigate('/contact')}
            >
              Get Free Consultation
            </button>
            <button 
              className="px-8 py-4 border-2 border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              onClick={() => navigate('/gallery')}
            >
              View Our Gallery
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
